// SourcesListPage — overview of all Sources in the workspace.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Source } from '@datahub/shared';
import { listSources, archiveSource, patchSource, createSource, listWorkspaces, bulkMoveSources, pauseAllTriggers } from '../api';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { Table } from '../components/Table';
import TopHeader from '../components/TopHeader';
import { TagChip } from '../lib/tag-colors';

/** Deterministic color per tag — same key=value always renders in the
 *  same hue so the eye builds a mental map ('client=acme' is always
 *  this teal, 'env=prod' always that amber). FNV-1a over `key=value`
 *  → hue in OKLCH; chroma + lightness fixed for AA contrast in both
 *  light and dark themes. */
// tagColor() removed — now using shared TagChip from lib/tag-colors.tsx
// so every chip across the workspace shares one color per tag KEY
// (env=*, system=*, …). Operators build visual memory: same color =
// same key everywhere.

function RelTime({ iso }: { iso: string }): React.ReactElement {
  const ms = Date.now() - Date.parse(iso);
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.round(s / 60);
  const h = Math.round(m / 60);
  const d = Math.round(h / 24);
  let label: string;
  if (s < 60) label = `${s}s ago`;
  else if (m < 60) label = `${m}m ago`;
  else if (h < 24) label = `${h}h ago`;
  else label = `${d}d ago`;
  return <span title={new Date(iso).toLocaleString()}>{label}</span>;
}

/** Compact chips for ACTIVE input kinds only — paused inputs aren't
 *  rendered, so the column reads as 'what's actually wired right now'.
 *  Email shown as the @ symbol (visually instant); other kinds use 2-3
 *  letter codes. Hover title gives the full kind for accessibility. */
function InputBadges({ inputs }: { inputs: Source['inputs'] }): React.ReactElement | null {
  const KIND_META: Record<string, { label: string; color: string }> = {
    webhook:     { label: 'WH',  color: 'oklch(0.55 0.16 240)' }, // blue
    file_upload: { label: 'UP',  color: 'oklch(0.55 0.18 145)' }, // green
    api_pull:    { label: 'API', color: 'oklch(0.55 0.18 290)' }, // purple
    email:       { label: '@',   color: 'oklch(0.60 0.16 60)'  }, // amber, glyph
    manual:      { label: 'MN',  color: 'oklch(0.50 0.02 0)'   }, // gray
  };
  const active = inputs.filter(i => i.status === 'active');
  if (active.length === 0) return <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>—</span>;
  return (
    <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {active.map(i => {
        const meta = KIND_META[i.kind] ?? KIND_META.manual!;
        return (
          <span
            key={i.input_id}
            title={i.kind}
            style={{
              padding: '1px 5px',
              fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600,
              letterSpacing: '0.04em',
              border: `1px solid ${meta.color}`,
              borderRadius: 3,
              background: meta.color,
              color: 'white',
            }}
          >{meta.label}</span>
        );
      })}
    </div>
  );
}

type ViewMode = 'cards' | 'table';

function readViewMode(): ViewMode {
  if (typeof localStorage === 'undefined') return 'cards';
  const v = localStorage.getItem('datahub-sources-view');
  return v === 'table' ? 'table' : 'cards';
}

export default function SourcesListPage() {
  const toast = useToast();
  const [sources, setSources] = useState<Source[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [archiving, setArchiving] = useState(false);
  // Archived sources live on a dedicated /sources/archive page now; the
  // main list always hides them so the everyday view stays clean.
  const showArchived = false;
  const [seeding, setSeeding] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(readViewMode);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('datahub-sources-view', viewMode);
  }, [viewMode]);

  // `/` focuses the page-level search box. Cmd+K is handled by the global
  // CommandPalette mounted in main.tsx — no duplicate listener here.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const t = e.target as HTMLElement;
      const inInput = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (inInput) return;
      if (e.key === '/' && sources && sources.length > 0) {
        const search = document.querySelector<HTMLInputElement>('input[placeholder*="Search by name" i]');
        if (search) { search.focus(); e.preventDefault(); }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sources]);

  function reload(): void {
    listSources(200, showArchived).then(setSources).catch(e => setError(String(e)));
  }
  useEffect(() => { reload(); }, [showArchived]);

  async function restore(id: string) {
    const updated = await patchSource(id, { status: 'active' });
    setSources(srcs => srcs?.map(s => s.source_id === id ? updated : s) ?? null);
  }

  async function pauseTriggersForRow(s: Source): Promise<void> {
    const ok = await toast.confirm({
      title: `Pause every trigger on "${s.name}"?`,
      message: 'Active derive / forward_webhook / promote_to_table flips to paused. Ingestion keeps landing in storage; nothing fires downstream until resumed.',
      confirmLabel: 'Pause',
    });
    if (!ok) return;
    try {
      const r = await pauseAllTriggers(s.source_id);
      toast.show(`Paused ${r.paused} trigger${r.paused === 1 ? '' : 's'} on ${s.name}`, { kind: 'success' });
    } catch (e) {
      toast.show(`Pause failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  async function bulkPause(): Promise<void> {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const ok = await toast.confirm({
      title: `Pause every trigger on ${ids.length} source${ids.length === 1 ? '' : 's'}?`,
      message: 'Active triggers across the selected sources flip to paused. Ingestion keeps landing in storage; nothing fires downstream until resumed.',
      confirmLabel: 'Pause',
    });
    if (!ok) return;
    let total = 0;
    let failed = 0;
    await Promise.all(ids.map(async id => {
      try {
        const r = await pauseAllTriggers(id);
        total += r.paused;
      } catch { failed++; }
    }));
    if (failed > 0) {
      toast.show(`Paused ${total} trigger${total === 1 ? '' : 's'} · ${failed} source${failed === 1 ? '' : 's'} failed`, { kind: 'error' });
    } else {
      toast.show(`Paused ${total} trigger${total === 1 ? '' : 's'} across ${ids.length} source${ids.length === 1 ? '' : 's'}`, { kind: 'success' });
    }
    setSelected(new Set());
  }

  const filtered = useMemo(() => {
    if (!sources) return null;
    const matched = !query.trim() ? sources : (() => {
      const q = query.toLowerCase();
      return sources.filter(s => {
        const tagsStr = Object.entries(s.tags ?? {}).map(([k, v]) => `${k}=${v}`).join(' ').toLowerCase();
        const notesStr = (s.notes ?? '').toLowerCase();
        return s.name.toLowerCase().includes(q) || s.source_id.toLowerCase().includes(q) || tagsStr.includes(q) || notesStr.includes(q);
      });
    })();
    // Sort by last_event_at desc (active sources float to top), falling back to created_at.
    // Sources with NO last_event_at sink to the bottom but still rank against each other by created_at.
    return [...matched].sort((a, b) => {
      const aKey = a.last_event_at ?? '';
      const bKey = b.last_event_at ?? '';
      if (aKey && bKey) return bKey.localeCompare(aKey);
      if (aKey && !bKey) return -1;
      if (!aKey && bKey) return 1;
      return b.created_at.localeCompare(a.created_at);
    });
  }, [sources, query]);

  function toggleSel(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function seedDemo() {
    setSeeding(true);
    try {
      const s = await createSource('Demo — Acme Salesforce', { env: 'demo', client: 'acme', system: 'salesforce' });
      const wh = s.inputs.find(i => i.kind === 'webhook')?.url;
      if (wh) {
        const samples = [
          { event: 'deal.won', deal_id: 'D-1001', amount: '12500.00', user: 'alice' },
          { event: 'deal.won', deal_id: 'D-1002', amount: '8750.50', user: 'alice' },
          { event: 'deal.lost', deal_id: 'D-1003', amount: '22000', user: 'bob' },
          { event: 'contact.created', contact_id: 'C-501', email: 'jane@acme.com' },
          { event: 'deal.won', deal_id: 'D-1004', amount: '950', user: 'bob' },
        ];
        await Promise.all(samples.map(p => fetch(wh, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p),
        }).catch(() => undefined)));
      }
      navigate(`/sources/${s.source_id}`);
    } finally {
      setSeeding(false);
    }
  }

  async function bulkArchive() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = await toast.confirm({
      title: `Archive ${ids.length} source${ids.length === 1 ? '' : 's'}?`,
      message: `Webhooks return 410 Gone afterward. Events stay durably stored — restore the source to bring ingestion back.\n\nType ARCHIVE to confirm.`,
      destructive: true,
      confirmLabel: `Archive ${ids.length}`,
      requireType: 'ARCHIVE',
    });
    if (!ok) return;
    setArchiving(true);
    try {
      await Promise.all(ids.map(id => archiveSource(id)));
      setSources(srcs => srcs?.filter(s => !selected.has(s.source_id)) ?? null);
      setSelected(new Set());
      toast.show(`Archived ${ids.length} source${ids.length === 1 ? '' : 's'}.`, { kind: 'success' });
    } catch (e) {
      toast.show(`Bulk archive failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setArchiving(false);
    }
  }

  return (
    <div>
      <TopHeader env="Sources" />

      <div className="shell">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 420px', flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Sources</h1>
            {sources && sources.length > 0 && (
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name, id, or tag…"
                style={{
                  padding: '7px 12px', fontSize: 13, fontFamily: 'var(--ui)',
                  background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6,
                  outline: 'none', color: 'var(--ink)', minWidth: 280,
                }}
              />
            )}
            {sources && (
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-3)' }}>
                {filtered?.length ?? 0} / {sources.length}
              </span>
            )}
            <Link to="/sources/archive" style={{ fontSize: 12, color: 'var(--ink-3)', textDecoration: 'none', marginLeft: 4 }} title="View archived sources">
              archive →
            </Link>
            {/* View-mode toggle: cards (Drive-style grid) or compact table.
                Choice persists in localStorage. Operators with 100+ sources
                tend to prefer table; new operators with a handful prefer cards. */}
            <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden', marginLeft: 8 }}>
              <button
                onClick={() => setViewMode('cards')}
                title="Cards view"
                style={{
                  padding: '5px 10px', fontSize: 11, fontFamily: 'var(--mono)',
                  background: viewMode === 'cards' ? 'var(--ink)' : 'transparent',
                  color: viewMode === 'cards' ? 'var(--bg)' : 'var(--ink-3)',
                  border: 'none', borderRight: '1px solid var(--line)', cursor: 'pointer',
                }}
              >cards</button>
              <button
                onClick={() => setViewMode('table')}
                title="Table view"
                style={{
                  padding: '5px 10px', fontSize: 11, fontFamily: 'var(--mono)',
                  background: viewMode === 'table' ? 'var(--ink)' : 'transparent',
                  color: viewMode === 'table' ? 'var(--bg)' : 'var(--ink-3)',
                  border: 'none', cursor: 'pointer',
                }}
              >table</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/" className="btn primary" style={{ textDecoration: 'none' }}>Create Source →</Link>
          </div>
        </div>

        {/* Bulk-action bar — only renders WHEN something is selected.
            Selection itself happens via row checkboxes + the header
            checkbox below (which selects all visible). The previous
            'Select all visible (199)' chrome that always sat above the
            table was removed — it was permanent visual noise on a page
            that's mostly browse-and-click, not bulk-edit. */}
        {selected.size > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            background: 'var(--accent-soft)',
            border: '1px solid var(--accent-line)',
            borderRadius: 8, marginBottom: 14,
          }}>
            <span style={{ fontSize: 13, color: 'var(--ink)' }}>
              <strong>{selected.size}</strong> selected
            </span>
            <BulkMoveButton selectedIds={Array.from(selected)} onMoved={() => { setSelected(new Set()); reload(); }} />
            <button className="btn sm" onClick={() => { void bulkPause(); }}>
              Pause triggers · {selected.size}
            </button>
            <button className="btn sm" onClick={() => { void bulkArchive(); }} disabled={archiving}>
              {archiving ? 'Archiving…' : `Archive ${selected.size} source${selected.size === 1 ? '' : 's'}`}
            </button>
            <button className="btn sm ghost" onClick={() => setSelected(new Set())}>Clear</button>
            {query.trim() && filtered && filtered.length < (sources?.length ?? 0) && (
              <span style={{ marginLeft: 'auto', fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                filtered to "{query.trim()}"
              </span>
            )}
          </div>
        )}

        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

        {!sources && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>}

        {sources && sources.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10, color: 'var(--ink-3)' }}>
            <div style={{ fontWeight: 500, color: 'var(--ink)', marginBottom: 6, fontSize: 15 }}>No Sources yet</div>
            <div style={{ fontSize: 13, marginBottom: 18 }}>A Source is a receiving endpoint — it gets a webhook URL on create.</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
              <Link to="/" className="btn primary" style={{ textDecoration: 'none' }}>Create your first Source →</Link>
              <button className="btn ghost" onClick={() => { void seedDemo(); }} disabled={seeding}>
                {seeding ? 'Seeding…' : 'Seed demo source (5 events)'}
              </button>
            </div>
          </div>
        )}

        {sources && sources.length > 0 && filtered && filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10, color: 'var(--ink-3)', fontSize: 13 }}>
            No sources match "{query}".
          </div>
        )}

        {filtered && filtered.length > 0 && viewMode === 'table' && (
          <Table<Source>
            rows={filtered}
            getRowKey={s => s.source_id}
            onRowClick={s => navigate(`/sources/${s.source_id}`)}
            rowStyle={s => ({ opacity: s.status === 'archived' ? 0.55 : 1 })}
            columns={[
              {
                id: 'select', label: '', width: 40, stopPropagation: true,
                headerRender: () => (
                  <input
                    type="checkbox"
                    ref={el => {
                      if (!el || !filtered) return;
                      const allSelected = filtered.length > 0 && filtered.every(s => selected.has(s.source_id));
                      const noneSelected = filtered.every(s => !selected.has(s.source_id));
                      el.checked = allSelected;
                      el.indeterminate = !allSelected && !noneSelected;
                    }}
                    onChange={() => {
                      if (!filtered) return;
                      const allVisible = filtered.map(s => s.source_id);
                      const allSelected = allVisible.every(id => selected.has(id));
                      if (allSelected) {
                        setSelected(prev => { const n = new Set(prev); for (const id of allVisible) n.delete(id); return n; });
                      } else {
                        setSelected(prev => { const n = new Set(prev); for (const id of allVisible) n.add(id); return n; });
                      }
                    }}
                    title={filtered && filtered.every(s => selected.has(s.source_id)) ? 'Deselect all visible' : `Select all ${filtered?.length ?? 0} visible`}
                    style={{ cursor: 'pointer' }}
                  />
                ),
                render: s => s.status === 'archived'
                  ? <span />
                  : <input type="checkbox" checked={selected.has(s.source_id)} onChange={() => toggleSel(s.source_id)} />,
              },
              {
                id: 'name', label: 'name',
                render: s => (
                  <>
                    <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{s.name}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>
                      {s.source_id}
                      {s.status === 'archived' && <span style={{ marginLeft: 6, padding: '1px 5px', background: 'var(--bg-sunken)', borderRadius: 3, color: 'var(--warn, oklch(0.55 0.15 75))' }}>archived</span>}
                      {s.status === 'paused' && <span style={{ marginLeft: 6, padding: '1px 5px', background: 'oklch(0.94 0.06 70)', border: '1px solid oklch(0.78 0.10 70)', borderRadius: 3, color: 'oklch(0.40 0.10 60)' }}>paused</span>}
                      {s.slug_alias && <span style={{ marginLeft: 6, padding: '1px 5px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 3, color: 'var(--accent)', fontSize: 10 }}>/h/{s.slug_alias}</span>}
                    </div>
                  </>
                ),
              },
              {
                id: 'tags', label: 'tags', stopPropagation: true,
                render: s => Object.keys(s.tags ?? {}).length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.entries(s.tags).slice(0, 3).map(([k, v]) => (
                      <TagChip
                        key={k} k={k} v={v} compact
                        onClick={() => setQuery(`${k}=${v}`)}
                        title={`Filter to ${k}=${v}`}
                      />
                    ))}
                    {Object.keys(s.tags).length > 3 && <span style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>+{Object.keys(s.tags).length - 3}</span>}
                  </div>
                ) : <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>,
              },
              { id: 'inputs', label: 'inputs', width: 140, render: s => <InputBadges inputs={s.inputs} /> },
              {
                id: 'last_event', label: 'last event', mono: true, width: 110,
                render: s => <span style={{ color: s.last_event_at ? 'var(--ink-2)' : 'var(--ink-3)' }}>{s.last_event_at ? <RelTime iso={s.last_event_at} /> : 'never'}</span>,
              },
              {
                id: 'created', label: 'created', mono: true, width: 110,
                render: s => <span style={{ color: 'var(--ink-3)' }}><RelTime iso={s.created_at} /></span>,
              },
              {
                id: 'actions', label: '', align: 'right', width: 110, stopPropagation: true,
                render: s => <RowActions source={s} onRestore={() => restore(s.source_id)} onPause={() => { void pauseTriggersForRow(s); }} />,
              },
            ]}
          />
        )}

        {filtered && filtered.length > 0 && viewMode === 'cards' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {filtered.map(s => {
              const isSelected = selected.has(s.source_id);
              const isArchived = s.status === 'archived';
              return (
                <div
                  key={s.source_id}
                  style={{
                    background: 'var(--bg-elev)',
                    border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--line)'}`,
                    borderRadius: 10, padding: 16,
                    position: 'relative',
                    opacity: isArchived ? 0.6 : 1,
                  }}
                >
                  {!isArchived && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSel(s.source_id)}
                      onClick={e => e.stopPropagation()}
                      style={{ position: 'absolute', top: 14, right: 14, cursor: 'pointer' }}
                    />
                  )}
                  {isArchived && (
                    <button
                      onClick={() => { void restore(s.source_id); }}
                      style={{ position: 'absolute', top: 12, right: 12, padding: '3px 8px', fontSize: 11, fontFamily: 'var(--mono)', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}
                      title="Restore — sets status back to active"
                    >restore</button>
                  )}
                  <Link
                    to={`/sources/${s.source_id}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block', paddingRight: 60 }}
                  >
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {s.source_id}
                      {isArchived && <span style={{ padding: '1px 5px', fontSize: 10, background: 'var(--bg-sunken)', borderRadius: 3, color: 'var(--warn, oklch(0.55 0.15 75))' }}>archived</span>}
                      {s.status === 'paused' && <span style={{ padding: '1px 5px', fontSize: 10, background: 'oklch(0.94 0.06 70)', border: '1px solid oklch(0.78 0.10 70)', borderRadius: 3, color: 'oklch(0.40 0.10 60)' }}>paused</span>}
                      {s.slug_alias && <span style={{ padding: '1px 5px', fontSize: 10, background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 3, color: 'var(--accent)' }}>/h/{s.slug_alias}</span>}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)' }}>{s.name}</div>
                    {s.notes && (
                      <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={s.notes}>
                        {s.notes}
                      </div>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <InputBadges inputs={s.inputs} />
                    </div>
                    {Object.keys(s.tags ?? {}).length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {Object.entries(s.tags).map(([k, v]) => (
                          <TagChip
                            key={k} k={k} v={v} compact
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuery(`${k}=${v}`); }}
                            title={`Filter to ${k}=${v}`}
                          />
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                      {s.last_event_at ? <>last event <RelTime iso={s.last_event_at} /> · </> : 'no events yet · '}
                      created <RelTime iso={s.created_at} />
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cmd+K command palette is mounted globally in main.tsx — no local copy. */}
    </div>
  );
}

/** Per-row action cluster: restore (archived sources only) + pause
 *  triggers (active sources only). Replaces the old workspace-wide
 *  overflow menu — operators now reach for the action on the row they
 *  actually want to touch, and the destructive workspace-wide pause-all
 *  is no longer exposed at the top of the list. */
function RowActions({ source, onRestore, onPause }: { source: Source; onRestore: () => void; onPause: () => void }): React.ReactElement {
  const archived = source.status === 'archived';
  if (archived) {
    return (
      <button
        onClick={onRestore}
        style={{ padding: '3px 8px', fontSize: 11, fontFamily: 'var(--mono)', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}
        title="Restore — sets status back to active"
      >restore</button>
    );
  }
  return (
    <button
      onClick={onPause}
      style={{ padding: '3px 8px', fontSize: 11, fontFamily: 'var(--mono)', background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}
      title="Pause every active trigger on this source"
    >pause triggers</button>
  );
}

/** Bulk-move dropdown — pick a target workspace from the user's
 *  memberships and POST /api/v1/sources:bulk-move. Loads workspaces
 *  on first open (lazy) so the page doesn't pay the cost when nothing
 *  is selected. */
function BulkMoveButton({ selectedIds, onMoved }: {
  selectedIds: string[];
  onMoved: () => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState<Array<{ workspace_id: string; name: string }> | null>(null);
  const [moving, setMoving] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    if (workspaces) return;
    listWorkspaces(false).then(ws => setWorkspaces(ws)).catch(() => setWorkspaces([]));
  }, [open, workspaces]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  async function move(targetId: string, targetName: string): Promise<void> {
    const ok = await toast.confirm({
      title: `Move ${selectedIds.length} source${selectedIds.length === 1 ? '' : 's'} to "${targetName}"?`,
      message: 'Sources stop being visible inside their current workspace and start being visible inside the target. No data is lost.',
      confirmLabel: 'Move',
    });
    if (!ok) return;
    setMoving(true);
    try {
      const r = await bulkMoveSources(selectedIds, targetId);
      const skipped = r.skipped.length;
      const skipNote = skipped > 0 ? ` (${skipped} skipped: ${r.skipped.slice(0, 3).map(s => s.reason).join(', ')}${skipped > 3 ? '…' : ''})` : '';
      toast.show(`Moved ${r.moved.length} source${r.moved.length === 1 ? '' : 's'} to ${targetName}${skipNote}`);
      setOpen(false);
      onMoved();
    } catch (e) {
      toast.show(`Move failed: ${String(e)}`);
    } finally {
      setMoving(false);
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn sm" onClick={() => setOpen(o => !o)} disabled={moving}>
        {moving ? 'Moving…' : `Move ${selectedIds.length} to…`}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          minWidth: 240, maxWidth: 320,
          background: 'var(--bg)', border: '1px solid var(--line)',
          borderRadius: 6, boxShadow: '0 4px 18px oklch(0.10 0 0 / 0.18)',
          zIndex: 20,
        }}>
          <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--ink-3)', borderBottom: '1px solid var(--line)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Target workspace
          </div>
          {workspaces === null && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-3)' }}>Loading…</div>
          )}
          {workspaces && workspaces.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-3)' }}>
              You're not in any workspace. <Link to="/workspaces" style={{ color: 'var(--ink)' }}>Create one →</Link>
            </div>
          )}
          {workspaces && workspaces.map(ws => (
            <button
              key={ws.workspace_id}
              onClick={() => { void move(ws.workspace_id, ws.name); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', fontSize: 12,
                background: 'transparent', color: 'var(--ink)',
                border: 'none', cursor: 'pointer',
                borderTop: '1px solid var(--line)',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elev)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 500 }}>{ws.name}</div>
              <div style={{ fontSize: 10.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{ws.workspace_id}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
